/**
 * Keeps the world high-score tables in step (docs/spec/07 §2): sends the ScoreBook's outbox and
 * fetches the tables. A score the server does not take this time - it is asleep, restarting or
 * busy - stays queued, is tried again with growing waits, and survives a reload.
 */

import type { Tables } from '../game/hiscores';
import type { Outgoing, ScoreBook } from './scoreBook';
import type { PostResult } from './worldScores';

export interface WorldApi {
  fetchTables(): Promise<Tables | null>;
  post(o: Outgoing): Promise<PostResult>;
}

export interface Timers {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

const browserTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

/** Waits between tries: long enough for a machine to boot, growing, then steady. */
export const RETRY_MS: readonly number[] = [2000, 5000, 10000, 20000, 40000, 60000];
/** Tries before waiting for the next score or the next visit (about eight minutes). */
export const MAX_TRIES = 12;

export class ScoreSync {
  private busy = false;
  private again = false;
  private tries = 0;
  private timer: unknown = null;

  constructor(private readonly book: ScoreBook, private readonly api: WorldApi, private readonly timers: Timers = browserTimers) {
    book.onSubmit(() => {
      this.tries = 0;
      void this.flush();
    });
  }

  /** At boot: send anything left from last time, and fetch the tables. */
  start(): void {
    void this.flush();
    void this.refresh();
  }

  async refresh(): Promise<void> {
    const tables = await this.api.fetchTables();
    if (tables) this.book.setWorld(tables);
  }

  /** Send every queued score, oldest first; on a 'retry', wait and go again from there. */
  async flush(): Promise<void> {
    if (this.busy) {
      this.again = true;
      return;
    }
    this.busy = true;
    if (this.timer !== null) this.timers.clear(this.timer);
    this.timer = null;
    let retry = false;
    let sent = false;
    try {
      for (const o of [...this.book.pending()]) {
        const result = await this.api.post(o);
        if (result === 'retry') {
          retry = true;
          break;
        }
        // Taken, or refused for good (the replay did not make the score): either way, done with it.
        this.book.settled(o.entry.id);
        sent ||= result === 'ok';
      }
    } finally {
      this.busy = false;
    }
    if (retry) this.schedule();
    else this.tries = 0;
    if (sent) void this.refresh();
    if (this.again) {
      this.again = false;
      void this.flush();
    }
  }

  private schedule(): void {
    if (this.timer !== null || this.tries >= MAX_TRIES) return;
    const ms = RETRY_MS[Math.min(this.tries, RETRY_MS.length - 1)] ?? 60000;
    this.tries++;
    this.timer = this.timers.set(() => {
      this.timer = null;
      void this.flush();
    }, ms);
  }
}
